import React, { MouseEvent, ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { reset } from '../../../../utils/styled-reset';
import { Button } from '../../../button/button';
import { CheckBox } from '../../../check-box/check-box';
import { Icon } from '../../../icon/icon';
import { Stack } from '../../../stack/stack';

type Props = {
  onClick: () => void;
  title: string;
  selected: boolean;
  children: ReactNode;
  disabled?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  titleBadge?: ReactNode;
};

const Card = (props: Props) => {
  const [internalExpanded, setInternalExpanded] = React.useState(false);

  const expanded = props.expanded ?? internalExpanded;

  const onExpandButtonClicked = (e: MouseEvent) => {
    e.stopPropagation();
    const next = !expanded;
    setInternalExpanded(next);
    props.onExpandedChange?.(next);
  };

  return (
    <Component
      disabled={props.disabled ?? false}
      expanded={expanded}
      selected={props.selected}
      onClick={props.onClick}
    >
      <Button variant="contained" onClick={onExpandButtonClicked}>
        <Icon fixedWidth={true} icon={expanded ? 'angle-down' : 'angle-right'} />
      </Button>

      <div>
        <StyledStack alignItems="center" direction="row" spacing={1}>
          <CheckBox
            aria-label={props.title}
            checked={props.selected}
            disabled={props.disabled ?? false}
            onChange={(e) => {
              props.onClick();
              e.stopPropagation();
            }}
          />
          <span>{props.title}</span>
          {props.titleBadge && <BadgeSpacer>{props.titleBadge}</BadgeSpacer>}
        </StyledStack>

        {expanded && <Content>{props.children}</Content>}
      </div>
    </Component>
  );
};

const Component = styled.div<{ disabled: boolean; selected: boolean; expanded: boolean }>`
  ${reset}

  display: flex;
  ${(props) =>
    props.disabled === false &&
    css`
      cursor: pointer;
    `}
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
  border-radius: 5px;
  margin: 0.5em;
  background-color: #fafafa;
  user-select: none;

  ${(props) =>
    props.selected === false &&
    css`
      label,
      p,
      ul,
      > span {
        opacity: 0.75;
      }
    `}

  button {
    background-color: ${(props) => props.theme.color.gray.lightest};

    ${(props) =>
      props.expanded &&
      css`
        background-color: ${props.theme.color.brand.light};
      `}
    border-radius: 3px 0 0 3px;

    &:hover {
      background-color: ${(props) => props.theme.color.brand.light} !important;
    }
  }

  > div {
    flex: 1;

    > label {
      ${(props) =>
        props.disabled === false &&
        css`
          cursor: pointer;
        `}
      padding: 0.75em;
      display: flex;
      align-items: baseline;

      span {
        font-weight: 500;
        font-size: 14pt;
        margin-top: 0;
      }

      input {
        ${(props) =>
          props.disabled === false &&
          css`
            cursor: pointer;
          `}
        margin-right: 1em;
      }
    }
  }

  &:hover {
    box-shadow:
      0 3px 6px rgba(0, 0, 0, 0.16),
      0 3px 6px rgba(0, 0, 0, 0.23);
    background-color: #f6f6f6;
  }
`;

const StyledStack = styled(Stack)`
  padding: 0.75em;
  width: 100%;
`;

const BadgeSpacer = styled.div`
  margin-left: auto;
`;

const Content = styled.div`
  padding: 0 1em 1em 1em;
  display: flex;
  flex-direction: column;
`;

export const ContentList = styled.ul`
  margin-left: 2em;
  list-style-type: none;

  li {
    display: flex;
    align-items: flex-start;
    gap: 0.5em;
    margin: 0.1em;
    font-size: 11pt;
  }

  li:first-child {
    margin-bottom: 1em;
  }
`;

export { Card };
